import {
    createSlice,
    createAsyncThunk
} from '@reduxjs/toolkit'
import api from '../../api/api'

export const add_to_card = createAsyncThunk(
    'card/add_to_card',
    async (info, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            // Check if there are existing products in cart
            const { card: { card_products } } = getState();
            const { auth: { userInfo } } = getState();
            
            // If cart has products, check if they're from the same seller
            if (card_products.length > 0) {
                const firstSellerId = card_products[0].sellerId;
                if (firstSellerId !== info.sellerId) {
                return rejectWithValue({
                        error: 'You can only add products from one seller at a time. Please complete or clear your current cart first.'
                });
                }
            }

            // Remove sellerId from the payload since it's not stored in the card model
            const { sellerId, ...cartInfo } = info;
            
            // Add sessionId for guest users
            if (!userInfo) {
                const sessionId = sessionStorage.getItem('guestSessionId') || `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                sessionStorage.setItem('guestSessionId', sessionId);
                cartInfo.sessionId = sessionId;
            }
            
            const { data } = await api.post('/home/product/add-to-card', cartInfo);
            
            // Clear any pending cart items from session storage after successful add
            sessionStorage.removeItem('pendingCartItem');
            
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Failed to add product to cart' })
        }
    }
)

export const get_card_products = createAsyncThunk(
    'card/get_card_products',
    async ({userId, sellerId, sessionId}, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            // console.log('Fetching cart products for user:', userId, 'seller:', sellerId, 'sessionId:', sessionId);
            
            let endpoint;
            if (userId && userId !== 'null' && userId !== 'undefined') {
                endpoint = `/home/product/get-card-product/${userId}/${sellerId}`;
            } else {
                // For guest users, use sessionId
                const guestSessionId = sessionId || sessionStorage.getItem('guestSessionId');
                if (!guestSessionId) {
                    return fulfillWithValue({
                        card_products: [],
                        price: 0,
                        card_product_count: 0,
                        shipping_fee: 0,
                        outofstock_products: [],
                        buy_product_item: 0
                    });
                }
                endpoint = `/home/product/get-card-product-guest/${guestSessionId}/${sellerId}`;
            }
            
            const { data } = await api.get(endpoint);
            
            // console.log('Cart data received:', data);
            
            // Handle case where no cart products exist
            if (!data.card_products || data.card_products.length === 0) {
                return fulfillWithValue({
                    card_products: [],
                    price: 0,
                    card_product_count: 0,
                    shipping_fee: 0,
                    outofstock_products: [],
                    buy_product_item: 0
                });
            }
            
            // Separate in-stock and out-of-stock products
            const processedProducts = data.card_products.map(seller => {
                const inStockProducts = [];
                const outOfStockProducts = [];
                
                seller.products.forEach(product => {
                    const stock = product.productInfo.stock;
                    const quantity = product.quantity;
                    
                    if (quantity > stock) {
                        outOfStockProducts.push({
                            ...product,
                            quantity: quantity, // Preserve the quantity
                            originalStock: stock // Keep track of original stock
                        });
                    } else {
                        inStockProducts.push(product);
                    }
                });
                
                return {
                    ...seller,
                    products: inStockProducts,
                    outOfStockProducts
                };
            });

            // Calculate totals: (discounted/bulk unit price × qty) + (addon sum per unit × qty)
            const price = processedProducts.reduce((total, seller) => {
                return total + seller.products.reduce((subtotal, product) => {
                    const quantity = product?.quantity || 1;
                    const info = product?.productInfo || {};
                    const basePrice = typeof info.price === 'number' ? info.price : (typeof product.price === 'number' ? product.price : 0);
                    const discount = typeof info.discount === 'number' ? info.discount : 0;
                    const bulkPricing = Array.isArray(info.bulkPricing) ? info.bulkPricing : [];

                    // addons total per unit
                    const addonsPrice = Array.isArray(product?.addons)
                        ? product.addons.reduce((sum, addon) => sum + (typeof addon?.price === 'number' ? addon.price : 0), 0)
                        : 0;

                    // compute final unit price with discount and bulk pricing
                    let finalUnitPrice = basePrice;
                    if (Array.isArray(bulkPricing) && bulkPricing.length > 0) {
                        const validBulkPricing = bulkPricing
                            .filter(bp => bp && bp.quantity != null && bp.price != null && !isNaN(bp.quantity) && !isNaN(bp.price))
                            .sort((a, b) => (b.quantity || 0) - (a.quantity || 0));
                        const applicable = validBulkPricing.find(bp => quantity >= (bp.quantity || 0));
                        if (applicable && typeof applicable.price === 'number') {
                            finalUnitPrice = applicable.price;
                        } else if (discount > 0) {
                            finalUnitPrice = basePrice - ((basePrice * discount) / 100);
                        }
                    } else if (discount > 0) {
                        finalUnitPrice = basePrice - ((basePrice * discount) / 100);
                    }

                    const totalProductPrice = (finalUnitPrice * quantity) + (addonsPrice * quantity);
                    return subtotal + totalProductPrice;
                }, 0);
            }, 0);

            const buy_product_item = processedProducts.reduce((total, seller) => {
                return total + seller.products.reduce((count, product) => {
                    return count + product.quantity;
                }, 0);
            }, 0);

            // Flatten out-of-stock products for display
            const outOfStockProducts = processedProducts.flatMap(seller => 
                seller.outOfStockProducts.map(product => ({
                    ...product,
                    sellerId: seller.sellerId,
                    shopName: seller.shopName
                }))
            );

            const result = {
                card_products: processedProducts.map(({ outOfStockProducts, ...rest }) => rest),
                price,
                card_product_count: buy_product_item,
                shipping_fee: data.shipping_fee,
                outofstock_products: outOfStockProducts,
                buy_product_item
            };
            
            // console.log('Processed cart result:', result);
            return fulfillWithValue(result);
        } catch (error) {
            // console.error('Error fetching cart products:', error);
            return rejectWithValue(error.response?.data || { error: 'Failed to get cart products' })
        }
    }
)

export const delete_card_product = createAsyncThunk(
    'card/delete_card_product',
    async (card_id, {
        rejectWithValue,
        fulfillWithValue
    }) => {
        try {
            const {
                data
            } = await api.delete(`/home/product/delete-card-product/${card_id}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Failed to delete cart product' })
        }
    }
)


export const quantity_inc = createAsyncThunk(
    'card/quantity_inc',
    async (card_id, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            // Find the product in the current state
            const state = getState();
            let product = null;
            let isOutOfStock = false;

            // Check in card_products
            for (const seller of state.card.card_products) {
                product = seller.products.find(p => p._id === card_id);
                if (product) break;
            }

            // If not found in card_products, check in outofstock_products
            if (!product) {
                product = state.card.outofstock_products.find(p => p._id === card_id);
                isOutOfStock = true;
            }

            // Allow quantity increment even if it exceeds current stock

            const { data } = await api.put(`/home/product/quantity-inc/${card_id}`);
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response?.data || error);
        }
    }
)

export const quantity_dec = createAsyncThunk(
    'card/quantity_dec',
    async (card_id, {
        rejectWithValue,
        fulfillWithValue
    }) => {
        try {
            const {
                data
            } = await api.put(`/home/product/quantity-dec/${card_id}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Failed to decrease quantity' })
        }
    }
)

export const add_to_wishlist = createAsyncThunk(
    'wishlist/add_to_wishlist',
    async (info, {
        rejectWithValue,
        fulfillWithValue
    }) => {
        try {
            const {
                data
            } = await api.post('/home/product/add-to-wishlist', info)
            // console.log(data)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Failed to add to wishlist' })
        }
    }
)

export const get_wishlist_products = createAsyncThunk(
    'wishlist/get_wishlist_products',
    async (userId, {
        rejectWithValue,
        fulfillWithValue
    }) => {
        try {
            const {
                data
            } = await api.get(`/home/product/get-wishlist-products/${userId}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const get_guest_wishlist_products = createAsyncThunk(
    'wishlist/get_guest_wishlist_products',
    async (sessionId, {
        rejectWithValue,
        fulfillWithValue
    }) => {
        try {
            const {
                data
            } = await api.get(`/home/product/get-wishlist-products-guest/${sessionId}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const remove_wishlist = createAsyncThunk(
    'wishlist/remove_wishlist',
    async (wishlistId, {
        rejectWithValue,
        fulfillWithValue
    }) => {
        try {
            const {
                data
            } = await api.delete(`/home/product/delete-wishlist-product/${wishlistId}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const update_cart_item = createAsyncThunk(
    'card/update_cart_item',
    async ({ cartId, addons }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.put(`/home/product/update-cart-item/${cartId}`, {
                addons
            });
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const cardReducer = createSlice({
    name: 'card',
    initialState: {
        card_products: [],
        card_product_count: 0,
        buy_product_item: 0,
        wishlist_count: 0,
        wishlist: [],
        price: 0,
        errorMessage: '',
        successMessage: '',
        shipping_fee: 0,
        outofstock_products: [],
        pendingCartItem: null, // Add this new state
        isLoading: false, // Add loading state
        isInitialLoad: true // Add initial load state
    },
    reducers: {
        messageClear: (state, _) => {
            state.errorMessage = ''
            state.successMessage = ''
        },
        reset_count: (state, _) => {
            state.card_product_count = 0
            state.wishlist_count = 0
        },
        // Add new reducer for pending cart items
        setPendingCartItem: (state, action) => {
            state.pendingCartItem = action.payload;
        },
        clearPendingCartItem: (state) => {
            state.pendingCartItem = null;
        },
        clearCart: (state) => {
            state.card_products = [];
            state.card_product_count = 0;
            state.buy_product_item = 0;
            state.price = 0;
            state.shipping_fee = 0;
            state.outofstock_products = [];
        },
        resetLoading: (state) => {
            state.isLoading = false;
            state.isInitialLoad = false;
        }
    },
    extraReducers: {
        [add_to_card.pending]: (state) => {
            state.isLoading = true;
        },
        [add_to_card.rejected]: (state, { payload }) => {
            state.errorMessage = payload?.error || 'Failed to add product to cart'
            // Clear pending cart item on rejection
            state.pendingCartItem = null
            state.isLoading = false;
        },
        [add_to_card.fulfilled]: (state, { payload }) => {
            state.successMessage = payload.message
            state.card_product_count = state.card_product_count + 1
            // Clear pending cart item on success
            state.pendingCartItem = null
            state.isLoading = false;
        },
        [get_card_products.pending]: (state) => {
            state.isLoading = true;
            state.isInitialLoad = false;
        },
        [get_card_products.fulfilled]: (state, {
            payload
        }) => {
            state.card_products = payload.card_products
            state.price = payload.price
            state.card_product_count = payload.card_product_count
            state.shipping_fee = payload.shipping_fee
            state.outofstock_products = payload.outofstock_products
            state.buy_product_item = payload.buy_product_item
            state.isLoading = false;
        },
        [get_card_products.rejected]: (state) => {
            state.isLoading = false;
            state.isInitialLoad = false;
        },
        [delete_card_product.pending]: (state) => {
            state.isLoading = true;
        },
        [delete_card_product.fulfilled]: (state, {
            payload
        }) => {
            state.successMessage = payload.message
            state.isLoading = false;
        },
        [delete_card_product.rejected]: (state) => {
            state.isLoading = false;
        },
        [quantity_inc.pending]: (state) => {
            state.isLoading = true;
        },
        [quantity_inc.fulfilled]: (state, {
            payload
        }) => {
            state.successMessage = payload.message
            state.isLoading = false;
        },
        [quantity_inc.rejected]: (state) => {
            state.isLoading = false;
        },
        [quantity_dec.pending]: (state) => {
            state.isLoading = true;
        },
        [quantity_dec.fulfilled]: (state, {
            payload
        }) => {
            state.successMessage = payload.message
            state.isLoading = false;
        },
        [quantity_dec.rejected]: (state) => {
            state.isLoading = false;
        },
        [add_to_wishlist.rejected]: (state, {
            payload
        }) => {
            state.errorMessage = payload?.error || 'Failed to add to wishlist'
        },
        [add_to_wishlist.fulfilled]: (state, {
            payload
        }) => {
            state.successMessage = payload.message
            state.wishlist_count = state.wishlist_count > 0 ? state.wishlist_count + 1 : 1
        },
        [get_wishlist_products.fulfilled]: (state, {
            payload
        }) => {
            state.wishlist = payload.wishlists
            state.wishlist_count = payload.wishlistCount
        },
        [get_guest_wishlist_products.fulfilled]: (state, {
            payload
        }) => {
            state.wishlist = payload.wishlists
            state.wishlist_count = payload.wishlistCount
        },
        [remove_wishlist.fulfilled]: (state, {
            payload
        }) => {
            state.successMessage = payload.message
            state.wishlist = state.wishlist.filter(p => p._id !== payload.wishlistId)
            state.wishlist_count = state.wishlist_count - 1
        },
        [update_cart_item.pending]: (state) => {
            state.isLoading = true;
        },
        [update_cart_item.fulfilled]: (state, { payload }) => {
            state.successMessage = payload.message;
            state.isLoading = false;
        },
        [update_cart_item.rejected]: (state, { payload }) => {
            state.errorMessage = payload?.error || 'Failed to update cart item';
            state.isLoading = false;
        },
    }
})


export const {
    messageClear,
    reset_count,
    setPendingCartItem,
    clearPendingCartItem,
    clearCart,
    resetLoading
} = cardReducer.actions
export default cardReducer.reducer